import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { TurnstileWidget } from "@/components/mera/TurnstileWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Persistent UTM capture: first-touch attribution stored for 30 days.
// If a visitor lands via a UTM URL, that context follows them through
// subsequent page visits until they submit or the TTL expires.
const ATTR_STORAGE_KEY = "mera_attr_v1";
const ATTR_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const readStoredAttribution = () => {
  try {
    const raw = window.localStorage.getItem(ATTR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > ATTR_TTL_MS) return null;
    return parsed.data || null;
  } catch {
    return null;
  }
};

const captureAttribution = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
  };
  const hasAny = Object.values(fromUrl).some((v) => v);
  if (hasAny) {
    const payload = {
      ...fromUrl,
      referrer: document.referrer || "",
      landing_path: window.location.pathname + window.location.search,
    };
    try {
      const record = { ts: Date.now(), data: payload };
      window.localStorage.setItem(ATTR_STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* storage full or blocked — ignore */
    }
    return payload;
  }
  // No UTM in URL — return any previously-stored first-touch, else just referrer.
  const stored = readStoredAttribution();
  if (stored) return stored;
  return {
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: "",
    referrer: document.referrer || "",
    landing_path: window.location.pathname + window.location.search,
  };
};

const BRAND_OPTS = [
  { id: "general", label: "All of MERA" },
  { id: "ryan", label: "Ryan" },
  { id: "folio", label: "Fol.io" },
  { id: "uday", label: "Uday" },
  { id: "eulr", label: "EULR" },
  { id: "kova", label: "KOVA" },
  { id: "cortex", label: "Cortex" },
  { id: "ptrn", label: "PTRN" },
  { id: "trnslt", label: "TRNSLT" },
  { id: "railwright", label: "Railwright" },
  { id: "vernai", label: "Vernai" },
  { id: "matr", label: "MATR" },
  { id: "partner", label: "Build with MERA" },
];

export const Waitlist = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    brand: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [widgetKey, setWidgetKey] = useState(0);
  const [attribution, setAttribution] = useState({});

  // Capture UTM + referrer once on mount so late navigation doesn't lose first-touch.
  useEffect(() => {
    setAttribution(captureAttribution());
  }, []);

  // Listen for pre-select requests from the Foundry accordion CTAs.
  useEffect(() => {
    const onPreselect = (e) => {
      const brand = e?.detail?.brand;
      if (!brand) return;
      // Only accept known brand ids so a rogue event can't corrupt state.
      const isKnown = BRAND_OPTS.some((b) => b.id === brand);
      if (!isKnown) return;
      setForm((prev) => ({ ...prev, brand }));
      // Move focus to the name input so keyboard users land inside the form.
      const nameEl = document.querySelector('[data-testid="waitlist-input-name"]');
      if (nameEl && typeof nameEl.focus === "function") {
        setTimeout(() => nameEl.focus({ preventScroll: true }), 350);
      }
    };
    window.addEventListener("mera:waitlist-preselect", onPreselect);
    return () => window.removeEventListener("mera:waitlist-preselect", onPreselect);
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        ...form,
        ...attribution,
        turnstile_token: turnstileToken || "",
      });
      setDone(true);
      toast.success("You're on the list. We'll be in touch.");
      setForm({ name: "", email: "", brand: "general", message: "", });
      setTurnstileToken(null);
      setWidgetKey((k) => k + 1);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many submissions. Please try again in a minute.");
      } else if (status === 400) {
        toast.error(
          err?.response?.data?.detail ||
            "Verification failed. Please refresh and try again."
        );
        setTurnstileToken(null);
        setWidgetKey((k) => k + 1);
      } else {
        toast.error(
          err?.response?.data?.detail?.[0]?.msg ||
            "Could not submit. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="px-6 md:px-10 py-24 md:py-32 border-t border-black/10"
    >
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5">
          <div className="font-mono-label text-[11px] text-black/55">§ 08 · Join the waitlist</div>
          <h2 className="mt-6 font-serif-display text-[44px] md:text-[64px] leading-[1.02] tracking-tight">
            Step into <span className="italic-accent">the ecosystem.</span>
          </h2>
          <p
            data-testid="waitlist-lede"
            className="mt-6 max-w-[460px] font-serif-display italic text-[18px] md:text-[20px] leading-relaxed text-black/75"
          >
            Occasionally MERA partners with organizations building human-centered
            AI — applying the same methodology and philosophy behind the
            ecosystem to problems worth solving.
          </p>
          <p className="mt-6 max-w-[460px] font-serif-display italic text-[18px] md:text-[20px] leading-relaxed text-black/75">
            Reserve early access to Ryan and Fol.io, or open an institutional
            conversation around Uday. We answer every note ourselves — no funnels,
            no auto-replies.
          </p>

          <div className="mt-10 border-t border-black/10 pt-6">
            <div className="font-mono-label text-[10px] text-black/55 mb-3">Contact</div>
            <a
              href="mailto:hello@meratechnology.com"
              data-testid="waitlist-email-link"
              className="font-serif-display text-[22px] text-black hover:opacity-60 transition-opacity"
            >
              hello@meratechnology.com
            </a>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7">
          <form
            data-testid="waitlist-form"
            onSubmit={onSubmit}
            className="bg-white border border-black/10 p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-mono-label text-[10px] text-black/55 block mb-2">
                  Name
                </label>
                <input
                  data-testid="waitlist-input-name"
                  className="mera-input"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Ada Lovelace"
                  required
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/55 block mb-2">
                  Email
                </label>
                <input
                  data-testid="waitlist-input-email"
                  type="email"
                  className="mera-input"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="ada@institution.org"
                  required
                />
              </div>
            </div>

            <div className="mt-8">
              <label className="font-mono-label text-[10px] text-black/55 block mb-3">
                Interested in
              </label>
              <div className="flex flex-wrap gap-2">
                {BRAND_OPTS.map((b) => (
                  <button
                    type="button"
                    key={b.id}
                    data-testid={`waitlist-brand-${b.id}`}
                    onClick={() => setForm({ ...form, brand: b.id })}
                    className={`px-4 py-2 rounded-full text-[12px] font-mono-label transition-all ${
                      form.brand === b.id
                        ? "bg-black text-white border border-black"
                        : "bg-transparent text-black/65 border border-black/15 hover:border-black/50"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <label className="font-mono-label text-[10px] text-black/55 block mb-2">
                A line about you (optional)
              </label>
              <textarea
                data-testid="waitlist-input-message"
                className="mera-input mera-textarea"
                rows={3}
                value={form.message}
                onChange={onChange("message")}
                placeholder="What you're building, what you need, where you are."
              />
            </div>

            <div className="mt-8">
              <TurnstileWidget
                key={widgetKey}
                onToken={(t) => setTurnstileToken(t)}
                onError={(m) => {
                  setTurnstileToken(null);
                  if (m) toast.error(m);
                }}
              />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 justify-between">
              <div className="font-mono-label text-[10px] text-black/45">
                We reply within 48 hours · zero spam, by principle
              </div>
              <button
                type="submit"
                disabled={submitting || turnstileToken === null}
                data-testid="waitlist-submit"
                className="mera-btn"
                title={turnstileToken === null ? "Verifying you're human…" : undefined}
              >
                <span className="dot" />
                {done ? (
                  <>
                    Added <Check size={16} strokeWidth={1.5} />
                  </>
                ) : submitting ? (
                  "Sending…"
                ) : turnstileToken === null ? (
                  "Verifying you're human…"
                ) : (
                  <>
                    Request access <ArrowUpRight size={16} strokeWidth={1.5} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
